import axios from 'axios';
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Link as RouterLink } from 'react-router-dom';
import { AdminNav } from '../AdminNav';
import { apiUrl } from '../../config';
import {
  AppointmentAvailabilityRuleDto,
  AppointmentBookingDto,
  AppointmentEvaluationLinkDto,
  AppointmentStatus,
  CancelAppointmentBookingRequest,
  jobTypeLabel,
} from '../../interfaces/appointments';
import { EvaluationsApiResponse } from '../../interfaces/EvaluationApiResponse';
import PickList from './PickList';
import { computeCalendarHourRange, parseClockToMinutes } from './calendarRange';
import { bookingCountColor, countNonHotelBookings } from './bookingCount';
import { addDays, endOfWeek, format, startOfDay, startOfWeek } from 'date-fns';

const statusLabel = (status: AppointmentStatus): string => {
  switch (status) {
    case AppointmentStatus.Pending:
      return 'Til verifisering';
    case AppointmentStatus.Confirmed:
      return 'Bekreftet';
    case AppointmentStatus.Rejected:
      return 'Avvist';
    case AppointmentStatus.CancelledByAdmin:
      return 'Avlyst';
    default:
      return 'Ukjent';
  }
};

const tireRatingLabel = (rating?: string): string => {
  switch ((rating ?? '').toLowerCase()) {
    case 'bra':
      return 'Bra';
    case 'middels':
      return 'Middels';
    case 'darlig':
    case 'dårlig':
      return 'Dårlig';
    default:
      return rating ?? 'Ukjent';
  }
};

type CancelDialogState = {
  open: boolean;
  appointmentId: number | null;
  reason: string;
};

type BookingViewMode = 'day' | 'week' | 'monthList' | 'search';
type WeekOffset = -1 | 0 | 1;
type CalendarSlotRange = {
  startSlot: number;
  endSlot: number;
};

const MIN_SEARCH_LENGTH = 3;
const SLOT_MINUTES = 15;
const SLOT_HEIGHT_PX = 20;

const isWeekend = (date: Date): boolean => date.getDay() === 0 || date.getDay() === 6;
const moveToWeekday = (date: Date, direction: 1 | -1): Date => {
  let candidate = addDays(date, direction);
  while (isWeekend(candidate)) {
    candidate = addDays(candidate, direction);
  }
  return candidate;
};

const buildCalendarSlotRange = (
  appointment: AppointmentBookingDto,
  calendarStartHour: number,
  calendarEndHour: number,
): CalendarSlotRange | null => {
  const bookingStartMinutes = parseClockToMinutes(appointment.time);
  if (bookingStartMinutes === null) {
    return null;
  }

  const dayStartMinutes = calendarStartHour * 60;
  const dayEndMinutes = calendarEndHour * 60;
  const bookingEndMinutes = bookingStartMinutes + appointment.durationMinutes;

  if (bookingEndMinutes <= dayStartMinutes || bookingStartMinutes >= dayEndMinutes) {
    return null;
  }

  const startOffsetMinutes = Math.max(0, bookingStartMinutes - dayStartMinutes);
  const endOffsetMinutes = Math.min(dayEndMinutes - dayStartMinutes, bookingEndMinutes - dayStartMinutes);

  const startSlot = Math.floor(startOffsetMinutes / SLOT_MINUTES);
  const endSlot = Math.max(startSlot + 1, Math.ceil(endOffsetMinutes / SLOT_MINUTES));
  return { startSlot, endSlot };
};


function AppointmentOverview() {
  const today = startOfDay(new Date());
  const initialSelectedDate = isWeekend(today) ? moveToWeekday(today, 1) : today;
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialSelectedDate);
  const [viewMode, setViewMode] = useState<BookingViewMode>('day');
  const [weekOffset, setWeekOffset] = useState<WeekOffset>(0);
  const [showCancelled, setShowCancelled] = useState(false);
  const [appointments, setAppointments] = useState<AppointmentBookingDto[]>([]);
  const [adjacentDayCounts, setAdjacentDayCounts] = useState<{ previous: number | null; next: number | null }>({
    previous: null,
    next: null,
  });
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedDayAppointmentId, setSelectedDayAppointmentId] = useState<number | null>(null);
  const [weekDetailAppointmentId, setWeekDetailAppointmentId] = useState<number | null>(null);
  const [pickListOpen, setPickListOpen] = useState(false);
  const [availabilityRules, setAvailabilityRules] = useState<AppointmentAvailabilityRuleDto[]>([]);

  useEffect(() => {
    // The enabled (open) period widens the calendar. Failing to load it just keeps the default range.
    axios.get<AppointmentAvailabilityRuleDto[]>(`${apiUrl}/admin/appointments/rules`)
      .then((response) => setAvailabilityRules(Array.isArray(response?.data) ? response.data : []))
      .catch(() => setAvailabilityRules([]));
  }, []);

  const [cancelDialog, setCancelDialog] = useState<CancelDialogState>({
    open: false,
    appointmentId: null,
    reason: '',
  });
  const [evaluationLinksByRegistration, setEvaluationLinksByRegistration] = useState<Record<string, AppointmentEvaluationLinkDto[]>>({});
  const [loadingEvaluationRegistration, setLoadingEvaluationRegistration] = useState<string | null>(null);
  const [evaluationErrorByRegistration, setEvaluationErrorByRegistration] = useState<Record<string, string>>({});
  const [evaluationDetailsByRegistration, setEvaluationDetailsByRegistration] = useState<Record<string, EvaluationsApiResponse | null>>({});
  const [loadingEvaluationDetailsRegistration, setLoadingEvaluationDetailsRegistration] = useState<string | null>(null);
  const [evaluationDetailsErrorByRegistration, setEvaluationDetailsErrorByRegistration] = useState<Record<string, string>>({});

  const filterVisibleAppointments = (items: AppointmentBookingDto[]) =>
    showCancelled
      ? items
      : items.filter((appointment) => appointment.status !== AppointmentStatus.CancelledByAdmin);

  const visibleAppointments = filterVisibleAppointments(appointments);

  const toIsoDate = (date: Date | null): string => (date ? format(date, 'yyyy-MM-dd') : '');
  const parseIsoDate = (date: string): Date => new Date(`${date}T00:00:00`);

  const sortAppointments = (items: AppointmentBookingDto[]): AppointmentBookingDto[] =>
    [...items].sort((a, b) => {
      const left = `${a.date}T${a.time}`;
      const right = `${b.date}T${b.time}`;
      return left.localeCompare(right);
    });

  const getWeekRange = (offset: WeekOffset): { start: Date; end: Date } => {
    const targetWeek = addDays(today, offset * 7);
    return {
      start: startOfWeek(targetWeek, { weekStartsOn: 1 }),
      end: endOfWeek(targetWeek, { weekStartsOn: 1 }),
    };
  };

  const fetchRangeAppointments = async (fromDate: string, toDate: string): Promise<AppointmentBookingDto[]> => {
    const response = await axios.get<AppointmentBookingDto[]>(`${apiUrl}/admin/appointments/range/${fromDate}/${toDate}`, {
      params: {
        includeCancelled: showCancelled,
      },
    });
    return sortAppointments(filterVisibleAppointments(response.data));
  };

  const fetchDayAppointments = async (date: string): Promise<AppointmentBookingDto[]> =>
    fetchRangeAppointments(date, date);

  const searchAppointments = async (term: string): Promise<AppointmentBookingDto[]> => {
    const response = await axios.get<AppointmentBookingDto[]>(
      `${apiUrl}/admin/appointments/search/${encodeURIComponent(term)}`,
      { params: { includeCancelled: showCancelled } },
    );
    return filterVisibleAppointments(response.data);
  };

  const runSearch = async () => {
    const term = searchInput.trim();
    if (term.length < MIN_SEARCH_LENGTH) {
      setAppointments([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      setAppointments(await searchAppointments(term));
    } catch (err: any) {
      setError(err?.response?.data ? String(err.response.data) : 'Kunne ikke søke i timebestillinger.');
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAppointmentsForCurrentView = async () => {
    if (viewMode === 'search') {
      await runSearch();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (viewMode === 'day') {
        if (!selectedDate) {
          setAppointments([]);
          return;
        }

        const dayAppointments = await fetchDayAppointments(toIsoDate(selectedDate));
        setAppointments(sortAppointments(dayAppointments));
        return;
      }

      if (viewMode === 'week') {
        const weekRange = getWeekRange(weekOffset);
        setAppointments(await fetchRangeAppointments(toIsoDate(weekRange.start), toIsoDate(weekRange.end)));
        return;
      }

      const monthStart = today;
      const monthEnd = addDays(monthStart, 29);
      const monthAppointments = await fetchRangeAppointments(toIsoDate(monthStart), toIsoDate(monthEnd));
      const now = new Date();
      setAppointments(monthAppointments.filter((item) => {
        const startAt = new Date(`${item.date}T${item.time}:00`);
        return startAt >= now;
      }));
    } catch (err: any) {
      if (err?.response?.data) {
        setError(String(err.response.data));
      } else if (viewMode === 'week') {
        setError('Kunne ikke hente timebestillinger for valgt uke.');
      } else if (viewMode === 'monthList') {
        setError('Kunne ikke hente kommende timebestillinger.');
      } else {
        setError('Kunne ikke hente timebestillinger for valgt dag.');
      }
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isActive = true;
    if (viewMode !== 'day' || !selectedDate) {
      setAdjacentDayCounts({ previous: null, next: null });
      return;
    }

    const previousDay = moveToWeekday(selectedDate, -1);
    const nextDay = moveToWeekday(selectedDate, 1);
    const previousDayIso = toIsoDate(previousDay);
    const nextDayIso = toIsoDate(nextDay);

    setAdjacentDayCounts({ previous: null, next: null });

    fetchRangeAppointments(previousDayIso, nextDayIso)
      .then((items) => {
        if (!isActive) {
          return;
        }

        const previousCount = items.filter((item) => item.date === previousDayIso).length;
        const nextCount = items.filter((item) => item.date === nextDayIso).length;
        setAdjacentDayCounts({ previous: previousCount, next: nextCount });
      })
      .catch(() => {
        if (isActive) {
          setAdjacentDayCounts({ previous: null, next: null });
        }
      });

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, showCancelled, viewMode]);

  useEffect(() => {
    if (viewMode === 'search') {
      return;
    }
    void loadAppointmentsForCurrentView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, showCancelled, viewMode, weekOffset]);

  useEffect(() => {
    if (viewMode !== 'search') {
      return;
    }

    if (searchInput.trim().length < MIN_SEARCH_LENGTH) {
      setAppointments([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      void runSearch();
    }, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, showCancelled, viewMode]);

  const moveSelectedDate = (direction: 1 | -1) => {
    setSelectedDate((previousSelectedDate) => {
      if (!previousSelectedDate) {
        return previousSelectedDate;
      }

      if (direction === -1) {
        const previousDate = moveToWeekday(previousSelectedDate, -1);
        if (startOfDay(previousDate) < today) {
          return previousSelectedDate;
        }
        return previousDate;
      }

      return moveToWeekday(previousSelectedDate, direction);
    });
  };

  const previousLabel = `Forrige dag (${adjacentDayCounts.previous ?? '-'})`;
  const nextLabel = `Neste dag (${adjacentDayCounts.next ?? '-'})`;
  const canMovePrevious = selectedDate
    ? startOfDay(moveToWeekday(selectedDate, -1)) >= today
    : false;
  const selectedWeekRange = getWeekRange(weekOffset);
  const monthRangeStart = today;
  const monthRangeEnd = addDays(monthRangeStart, 29);
  const weekRangeLabel = `${format(selectedWeekRange.start, 'dd.MM.yyyy')} - ${format(selectedWeekRange.end, 'dd.MM.yyyy')}`;
  const monthRangeLabel = `${format(monthRangeStart, 'dd.MM.yyyy')} - ${format(monthRangeEnd, 'dd.MM.yyyy')}`;
  const selectedDayIso = selectedDate ? toIsoDate(selectedDate) : null;
  const selectedDayBooking = selectedDayAppointmentId
    ? visibleAppointments.find((appointment) => appointment.id === selectedDayAppointmentId) ?? null
    : null;
  const weekDetailBooking = weekDetailAppointmentId
    ? visibleAppointments.find((appointment) => appointment.id === weekDetailAppointmentId) ?? null
    : null;
  const weekDays = Array.from({ length: 5 }, (_, index) => addDays(selectedWeekRange.start, index));
  const calendarDates = viewMode === 'week' ? weekDays.map((day) => toIsoDate(day)) : selectedDayIso ? [selectedDayIso] : [];
  const { startHour: calendarStartHour, endHour: calendarEndHour } = computeCalendarHourRange(
    calendarDates,
    visibleAppointments,
    availabilityRules,
  );
  const slotCount = ((calendarEndHour - calendarStartHour) * 60) / SLOT_MINUTES;
  const timelineHeightPx = slotCount * SLOT_HEIGHT_PX;
  const hourMarks = Array.from({ length: (calendarEndHour - calendarStartHour) + 1 }, (_, index) => calendarStartHour + index);

  const bookingBlockStyle = (status: AppointmentStatus, selected: boolean) => {
    if (status === AppointmentStatus.CancelledByAdmin) {
      return {
        backgroundColor: '#2f3744',
        borderColor: selected ? '#8bb6ff' : '#596980',
        color: '#e9eef5',
      };
    }

    if (status === AppointmentStatus.Pending) {
      return {
        backgroundColor: '#1f3f58',
        borderColor: selected ? '#7bb9ff' : '#4d8bbf',
        color: '#eff7ff',
      };
    }

    return {
      backgroundColor: '#1c4252',
      borderColor: selected ? '#7bb9ff' : '#4f93ac',
      color: '#eff8fb',
    };
  };

  useEffect(() => {
    if (loading) {
      return;
    }

    if (viewMode !== 'day') {
      return;
    }

    if (visibleAppointments.length === 0) {
      setSelectedDayAppointmentId(null);
      return;
    }

    const hasCurrentSelection = selectedDayAppointmentId !== null &&
      visibleAppointments.some((appointment) => appointment.id === selectedDayAppointmentId);

    if (!hasCurrentSelection) {
      setSelectedDayAppointmentId(visibleAppointments[0].id);
    }
  }, [loading, selectedDayAppointmentId, viewMode, visibleAppointments]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (weekDetailAppointmentId !== null &&
      !visibleAppointments.some((appointment) => appointment.id === weekDetailAppointmentId)) {
      setWeekDetailAppointmentId(null);
    }
  }, [loading, visibleAppointments, weekDetailAppointmentId]);

  const loadEvaluationLinks = async (registrationNumber: string) => {
    const normalizedRegistrationNumber = registrationNumber.trim().toUpperCase();
    // Bookings for an unknown customer have no registration number, so there is nothing to look up.
    if (!normalizedRegistrationNumber) {
      return;
    }

    if (evaluationLinksByRegistration[normalizedRegistrationNumber] || loadingEvaluationRegistration === normalizedRegistrationNumber) {
      return;
    }

    setLoadingEvaluationRegistration(normalizedRegistrationNumber);
    setEvaluationErrorByRegistration((prev) => {
      if (!prev[normalizedRegistrationNumber]) {
        return prev;
      }

      const next = { ...prev };
      delete next[normalizedRegistrationNumber];
      return next;
    });

    try {
      const response = await axios.get<AppointmentEvaluationLinkDto[]>(
        `${apiUrl}/admin/evaluations/vehicle/${normalizedRegistrationNumber}`,
      );
      setEvaluationLinksByRegistration((prev) => ({ ...prev, [normalizedRegistrationNumber]: response.data }));
      // Fetch the most recent full evaluation (if available) to show tire placement
      const latest = (response.data ?? []).filter(e => e.id !== null).sort((a, b) => (b.performedDate ?? '').localeCompare(a.performedDate ?? ''))[0];
      if (latest && latest.id !== null && !Object.prototype.hasOwnProperty.call(evaluationDetailsByRegistration, normalizedRegistrationNumber)) {
        try {
          setLoadingEvaluationDetailsRegistration(normalizedRegistrationNumber);
          const detailResp = await axios.get<EvaluationsApiResponse>(`${apiUrl}/admin/evaluations/${latest.id}`);
          setEvaluationDetailsByRegistration((prev) => ({ ...prev, [normalizedRegistrationNumber]: detailResp.data }));
        } catch {
          setEvaluationDetailsErrorByRegistration((prev) => ({
            ...prev,
            [normalizedRegistrationNumber]: 'Kunne ikke hente detaljert dekkvurdering for kjøretøyet.',
          }));
        } finally {
          setLoadingEvaluationDetailsRegistration(null);
        }
      }
    } catch {
      setEvaluationErrorByRegistration((prev) => ({
        ...prev,
        [normalizedRegistrationNumber]: 'Kunne ikke hente dekkvurderinger for kjøretøyet.',
      }));
    } finally {
      setLoadingEvaluationRegistration(null);
    }
  };

  useEffect(() => {
    if (selectedDayBooking) {
      void loadEvaluationLinks(selectedDayBooking.registrationNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDayBooking?.registrationNumber]);

  useEffect(() => {
    if (weekDetailBooking) {
      void loadEvaluationLinks(weekDetailBooking.registrationNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekDetailBooking?.registrationNumber]);

  const renderEvaluationLinks = (registrationNumber: string) => {
    const normalizedRegistrationNumber = registrationNumber.trim().toUpperCase();
    if (!normalizedRegistrationNumber) {
      return <Typography variant="body2" color="text.secondary">Ingen registreringsnummer.</Typography>;
    }

    const evaluations = (evaluationLinksByRegistration[normalizedRegistrationNumber] ?? [])
      .filter((evaluation) => evaluation.id !== null);
    const errorText = evaluationErrorByRegistration[normalizedRegistrationNumber];
    const hasLoaded = Object.prototype.hasOwnProperty.call(evaluationLinksByRegistration, normalizedRegistrationNumber);

    if (loadingEvaluationRegistration === normalizedRegistrationNumber) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">Laster dekkvurderinger...</Typography>
        </Stack>
      );
    }

    if (errorText) {
      return (
        <Stack spacing={1}>
          <Typography variant="body2" color="error">{errorText}</Typography>
          <Button size="small" variant="outlined" onClick={() => { void loadEvaluationLinks(normalizedRegistrationNumber); }}>
            Prøv igjen
          </Button>
        </Stack>
      );
    }

    if (!hasLoaded) {
      return (
        <Button size="small" variant="outlined" onClick={() => { void loadEvaluationLinks(normalizedRegistrationNumber); }}>
          Hent dekkvurderinger
        </Button>
      );
    }

    if (evaluations.length === 0) {
      return <Typography variant="body2" color="text.secondary">Ingen dekkvurderinger funnet.</Typography>;
    }

    return (
      <Stack spacing={1}>
        {evaluations.map((evaluation) => (
          <Button
            key={`${evaluation.id}-${evaluation.season ?? 'season'}`}
            size="small"
            variant="outlined"
            component={RouterLink}
            to={`/admin/auth/tirerating/${evaluation.id}`}
            sx={{ justifyContent: 'flex-start' }}
          >
            {`${evaluation.season ?? 'Ukjent sesong'} (${tireRatingLabel(evaluation.rating)})`}
          </Button>
        ))}
      </Stack>
    );
  };

  const submitCancellation = async () => {
    if (!cancelDialog.appointmentId) {
      return;
    }

    setError(null);
    setSuccess(null);

    const payload: CancelAppointmentBookingRequest = {
      reason: cancelDialog.reason.trim() || undefined,
      cancelledBy: 'Admin',
    };

    try {
      await axios.patch(`${apiUrl}/admin/appointments/${cancelDialog.appointmentId}/cancel`, payload);
      setSuccess('Booking avlyst.');
      setCancelDialog({ open: false, appointmentId: null, reason: '' });
      setWeekDetailAppointmentId(null);
      await loadAppointmentsForCurrentView();
    } catch (err: any) {
      if (err?.response?.data) {
        setError(String(err.response.data));
      } else {
        setError('Kunne ikke avlyse booking.');
      }
    }
  };

  const openCancelDialog = (appointmentId: number) => {
    setCancelDialog({
      open: true,
      appointmentId,
      reason: '',
    });
  };

  return (
    <Grid container>
      <Grid item xs={2}>
        <AdminNav />
      </Grid>
      <Grid item xs={10} sx={{ p: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Timebestillinger
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              value={viewMode}
              onChange={(_, value: BookingViewMode | null) => {
                if (value) {
                  setViewMode(value);
                }
              }}
              aria-label="Visningsmodus for timebestillinger"
            >
              <ToggleButton value="day">Dag</ToggleButton>
              <ToggleButton value="week">Uke</ToggleButton>
              <ToggleButton value="monthList">Neste 30 dager</ToggleButton>
              <ToggleButton value="search">Søk</ToggleButton>
            </ToggleButtonGroup>

            <FormControlLabel
              control={
                <Switch
                  checked={showCancelled}
                  onChange={(event) => setShowCancelled(event.target.checked)}
                />
              }
              label="Vis avlyste"
            />
            {loading && <Typography variant="body2">Laster...</Typography>}
          </Stack>

          {viewMode === 'day' && (
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => moveSelectedDate(-1)}
                disabled={!canMovePrevious}
              >
                {previousLabel}
              </Button>
              <DatePicker
                label="Dato"
                inputFormat="dd/MM/yyyy"
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                shouldDisableDate={(date) => isWeekend(date)}
                renderInput={(params) => <TextField {...params} />}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => moveSelectedDate(1)}
                disabled={!selectedDate}
              >
                {nextLabel}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPickListOpen(true)}
                disabled={!selectedDate || loading}
              >
                Plukkliste
              </Button>
            </Stack>
          )}

          {viewMode === 'week' && (
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
              <Button
                variant={weekOffset === -1 ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setWeekOffset(-1)}
              >
                Forrige uke
              </Button>
              <Button
                variant={weekOffset === 0 ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setWeekOffset(0)}
              >
                Denne uke
              </Button>
              <Button
                variant={weekOffset === 1 ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setWeekOffset(1)}
              >
                Neste uke
              </Button>
              <Typography variant="body2" color="text.secondary">
                Ukevisning: {weekRangeLabel} ({visibleAppointments.length} bookinger)
              </Typography>
            </Stack>
          )}

          {viewMode === 'monthList' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Kommende bookinger for perioden {monthRangeLabel} ({visibleAppointments.length} bookinger)
            </Typography>
          )}

          {viewMode === 'search' && (
            <TextField
              fullWidth
              size="small"
              label="Søk (navn, telefon eller regnr, minst 3 tegn)"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              sx={{ mb: 2 }}
            />
          )}

          {viewMode === 'monthList' || viewMode === 'search' ? (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {visibleAppointments.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {viewMode === 'search'
                    ? (searchInput.trim().length < MIN_SEARCH_LENGTH ? 'Skriv minst 3 tegn for å søke.' : 'Ingen bookinger funnet.')
                    : 'Ingen kommende bookinger funnet.'}
                </Typography>
              )}

              {visibleAppointments.map((appointment) => (
                <Paper key={appointment.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2">
                    {format(parseIsoDate(appointment.date), 'dd.MM.yyyy')} kl. {appointment.time} ({appointment.durationMinutes} min) - {appointment.customerName}{appointment.registrationNumber ? ` (${appointment.registrationNumber})` : ''} - {statusLabel(appointment.status)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Telefon: {appointment.customerPhone || '-'}
                    {jobTypeLabel(appointment.jobType) ? ` | Jobbtype: ${jobTypeLabel(appointment.jobType)}` : ''}
                    {appointment.customerNote ? ` | Merknad: ${appointment.customerNote}` : ''}
                  </Typography>
                  {viewMode === 'search' &&
                    (appointment.status === AppointmentStatus.Pending || appointment.status === AppointmentStatus.Confirmed) && (
                      <Box sx={{ mt: 1 }}>
                        <Button size="small" variant="outlined" color="error" onClick={() => openCancelDialog(appointment.id)}>
                          Avlys booking
                        </Button>
                      </Box>
                  )}
                </Paper>
              ))}
            </Stack>
          ) : (
            <>
              {visibleAppointments.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Ingen bookinger funnet.
                </Typography>
              )}

              {viewMode === 'day' && visibleAppointments.length > 0 && (
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mt: 2 }}>
                  <Paper variant="outlined" sx={{ p: 2, flex: 1.2, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Kalender {selectedDayIso ? format(parseIsoDate(selectedDayIso), 'dd.MM.yyyy') : ''}
                    </Typography>
                    {selectedDayIso && (
                      <Typography
                        variant="body2"
                        data-testid={`booking-count-${selectedDayIso}`}
                        sx={{ mb: 1, fontWeight: 600, color: bookingCountColor(countNonHotelBookings(visibleAppointments, selectedDayIso)) }}
                      >
                        {`Ikke hotell: ${countNonHotelBookings(visibleAppointments, selectedDayIso)}`}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex' }}>
                      <Box sx={{ width: 62, position: 'relative', height: `${timelineHeightPx}px` }}>
                        {hourMarks.map((hour) => (
                          <Typography
                            key={hour}
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              position: 'absolute',
                              top: `${((hour - calendarStartHour) * 60 * SLOT_HEIGHT_PX) / SLOT_MINUTES - 8}px`,
                              right: 8,
                            }}
                          >
                            {`${String(hour).padStart(2, '0')}:00`}
                          </Typography>
                        ))}
                      </Box>

                      <Box sx={{ flex: 1, position: 'relative', height: `${timelineHeightPx}px`, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: '#111722' }}>
                        {Array.from({ length: slotCount + 1 }, (_, slotIndex) => (
                          <Box
                            key={slotIndex}
                            sx={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              top: `${slotIndex * SLOT_HEIGHT_PX}px`,
                              borderTop: '1px solid',
                              borderColor: slotIndex % (60 / SLOT_MINUTES) === 0 ? '#3b4d67' : '#26374b',
                            }}
                          />
                        ))}

                        {visibleAppointments.map((appointment) => {
                          const slotRange = buildCalendarSlotRange(appointment, calendarStartHour, calendarEndHour);
                          if (!slotRange) {
                            return null;
                          }

                          const selected = selectedDayAppointmentId === appointment.id;
                          const palette = bookingBlockStyle(appointment.status, selected);
                          return (
                            <Box
                              key={appointment.id}
                              data-testid={`day-calendar-booking-${appointment.id}`}
                              role="button"
                              tabIndex={0}
                              onClick={() => setSelectedDayAppointmentId(appointment.id)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  setSelectedDayAppointmentId(appointment.id);
                                }
                              }}
                              sx={{
                                position: 'absolute',
                                top: `${(slotRange.startSlot * SLOT_HEIGHT_PX) + 1}px`,
                                left: 6,
                                right: 6,
                                height: `${Math.max((slotRange.endSlot - slotRange.startSlot) * SLOT_HEIGHT_PX - 2, 16)}px`,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: palette.borderColor,
                                backgroundColor: palette.backgroundColor,
                                color: palette.color,
                                px: 1,
                                py: 0.5,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                boxShadow: selected ? '0 0 0 1px #9dceff inset' : 'none',
                              }}
                            >
                              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, lineHeight: 1.2 }}>
                                {appointment.time} · {appointment.customerName}
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.9, lineHeight: 1.1 }}>
                                {appointment.registrationNumber}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Detaljer</Typography>
                    {!selectedDayBooking && (
                      <Typography variant="body2" color="text.secondary">
                        Velg en booking i kalenderen for å se detaljer.
                      </Typography>
                    )}

                    {selectedDayBooking && (
                      <Stack spacing={1.5}>
                        <Typography variant="body2"><strong>Tid:</strong> {selectedDayBooking.time} ({selectedDayBooking.durationMinutes} min)</Typography>
                        <Typography variant="body2"><strong>Status:</strong> {statusLabel(selectedDayBooking.status)}</Typography>
                        <Typography variant="body2"><strong>Kunde:</strong> {selectedDayBooking.customerName}</Typography>
                        <Typography variant="body2"><strong>Telefon:</strong> {selectedDayBooking.customerPhone || '-'}</Typography>
                        <Typography variant="body2"><strong>Regnr:</strong> {selectedDayBooking.registrationNumber || '-'}</Typography>
                        <Typography variant="body2"><strong>Jobbtype:</strong> {jobTypeLabel(selectedDayBooking.jobType) || 'Ikke angitt'}</Typography>
                        <Typography variant="body2"><strong>Merknad:</strong> {selectedDayBooking.customerNote || '-'}</Typography>
                        {selectedDayBooking.smsFailed && (
                          <Alert severity="warning">
                            SMS-feil: {selectedDayBooking.smsErrorDescription || 'Ukjent feil'}
                          </Alert>
                        )}
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Dekkvurderinger</strong>
                          </Typography>
                          {/* Show placement from most recent evaluation if available (above evaluation links) */}
                          {(() => {
                            const reg = selectedDayBooking.registrationNumber.trim().toUpperCase();
                            if (!reg) {
                              return null;
                            }

                            const evalDetail = evaluationDetailsByRegistration[reg];
                            const evalError = evaluationDetailsErrorByRegistration[reg];
                            if (loadingEvaluationDetailsRegistration === reg) {
                              return <Typography variant="body2" color="text.secondary">Laster dekkplassering...</Typography>;
                            }

                            if (evalError) {
                              return <Typography variant="body2" color="error">{evalError}</Typography>;
                            }

                            // Prefer appointment-level vehicle location if present (returned from backend), fallback to latest evaluation vehicle.location
                            const appointmentLevelLocation = selectedDayBooking?.vehicleLocation;
                            const finalLocation = appointmentLevelLocation ?? evalDetail?.vehicle?.location ?? 'Ukjent';
                            if (!finalLocation) {
                              return null;
                            }

                            return (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}><strong>Plassering: {finalLocation}</strong></Typography>
                              </Box>
                            );
                          })()}
                          {renderEvaluationLinks(selectedDayBooking.registrationNumber)}
                        </Box>
                        {(selectedDayBooking.status === AppointmentStatus.Pending ||
                          selectedDayBooking.status === AppointmentStatus.Confirmed) && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              sx={{ alignSelf: 'flex-start' }}
                              onClick={() => openCancelDialog(selectedDayBooking.id)}
                            >
                              Avlys booking
                            </Button>
                        )}
                      </Stack>
                    )}
                  </Paper>
                </Stack>
              )}

              {viewMode === 'week' && visibleAppointments.length > 0 && (
                <Paper variant="outlined" sx={{ mt: 2, p: 2, overflowX: 'auto' }}>
                  <Box sx={{ minWidth: 840 }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `62px repeat(${weekDays.length}, minmax(150px, 1fr))`,
                        gap: 1,
                      }}
                    >
                      <Box />
                      {weekDays.map((weekDay) => (
                        <Box key={toIsoDate(weekDay)} sx={{ textAlign: 'center' }}>
                          <Typography
                            variant="caption"
                            data-testid={`booking-count-${toIsoDate(weekDay)}`}
                            sx={{ display: 'block', fontWeight: 600, color: bookingCountColor(countNonHotelBookings(visibleAppointments, toIsoDate(weekDay))) }}
                          >
                            {`Ikke hotell: ${countNonHotelBookings(visibleAppointments, toIsoDate(weekDay))}`}
                          </Typography>
                          <Typography variant="subtitle2" align="center">
                            {format(weekDay, 'EEE dd.MM')}
                          </Typography>
                        </Box>
                      ))}

                      <Box sx={{ position: 'relative', height: `${timelineHeightPx}px` }}>
                        {hourMarks.map((hour) => (
                          <Typography
                            key={hour}
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              position: 'absolute',
                              top: `${((hour - calendarStartHour) * 60 * SLOT_HEIGHT_PX) / SLOT_MINUTES - 8}px`,
                              right: 8,
                            }}
                          >
                            {`${String(hour).padStart(2, '0')}:00`}
                          </Typography>
                        ))}
                      </Box>

                      {weekDays.map((weekDay) => {
                        const weekDayIso = toIsoDate(weekDay);
                        const dayAppointments = visibleAppointments.filter((appointment) => appointment.date === weekDayIso);
                        return (
                          <Box
                            key={weekDayIso}
                            sx={{
                              position: 'relative',
                              height: `${timelineHeightPx}px`,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              backgroundColor: '#111722',
                            }}
                          >
                            {Array.from({ length: slotCount + 1 }, (_, slotIndex) => (
                              <Box
                                key={`${weekDayIso}-${slotIndex}`}
                                sx={{
                                  position: 'absolute',
                                  left: 0,
                                  right: 0,
                                  top: `${slotIndex * SLOT_HEIGHT_PX}px`,
                                  borderTop: '1px solid',
                                  borderColor: slotIndex % (60 / SLOT_MINUTES) === 0 ? '#3b4d67' : '#26374b',
                                }}
                              />
                            ))}

                            {dayAppointments.map((appointment) => {
                              const slotRange = buildCalendarSlotRange(appointment, calendarStartHour, calendarEndHour);
                              if (!slotRange) {
                                return null;
                              }

                              const selected = weekDetailAppointmentId === appointment.id;
                              const palette = bookingBlockStyle(appointment.status, selected);
                              return (
                                <Box
                                  key={appointment.id}
                                  data-testid={`week-calendar-booking-${appointment.id}`}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => {
                                    setWeekDetailAppointmentId(appointment.id);
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      setWeekDetailAppointmentId(appointment.id);
                                    }
                                  }}
                                  sx={{
                                    position: 'absolute',
                                    top: `${(slotRange.startSlot * SLOT_HEIGHT_PX) + 1}px`,
                                    left: 5,
                                    right: 5,
                                    height: `${Math.max((slotRange.endSlot - slotRange.startSlot) * SLOT_HEIGHT_PX - 2, 14)}px`,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: palette.borderColor,
                                    backgroundColor: palette.backgroundColor,
                                    color: palette.color,
                                    px: 0.75,
                                    py: 0.5,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    boxShadow: selected ? '0 0 0 1px #9dceff inset' : 'none',
                                  }}
                                >
                                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, lineHeight: 1.2 }}>
                                    {appointment.time}
                                  </Typography>
                                  <Typography variant="caption" sx={{ lineHeight: 1.1 }}>
                                    {appointment.customerName}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Paper>
              )}
            </>
          )}
        </Paper>

      </Grid>

      <PickList
        open={pickListOpen && viewMode === 'day'}
        onClose={() => setPickListOpen(false)}
        dateLabel={selectedDayIso ? format(parseIsoDate(selectedDayIso), 'dd.MM.yyyy') : ''}
        appointments={appointments}
      />

      <Dialog
        open={Boolean(weekDetailBooking) && viewMode === 'week'}
        onClose={() => setWeekDetailAppointmentId(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Bookingdetaljer</DialogTitle>
        <DialogContent>
          {!weekDetailBooking && (
            <Typography variant="body2" color="text.secondary">
              Ingen booking valgt.
            </Typography>
          )}

          {weekDetailBooking && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Typography variant="body2">
                <strong>Dato:</strong> {format(parseIsoDate(weekDetailBooking.date), 'dd.MM.yyyy')}
              </Typography>
              <Typography variant="body2">
                <strong>Tid:</strong> {weekDetailBooking.time} ({weekDetailBooking.durationMinutes} min)
              </Typography>
              <Typography variant="body2"><strong>Status:</strong> {statusLabel(weekDetailBooking.status)}</Typography>
              <Typography variant="body2"><strong>Kunde:</strong> {weekDetailBooking.customerName}</Typography>
              <Typography variant="body2"><strong>Telefon:</strong> {weekDetailBooking.customerPhone || '-'}</Typography>
              <Typography variant="body2"><strong>Regnr:</strong> {weekDetailBooking.registrationNumber || '-'}</Typography>
              <Typography variant="body2"><strong>Jobbtype:</strong> {jobTypeLabel(weekDetailBooking.jobType) || 'Ikke angitt'}</Typography>
              <Typography variant="body2"><strong>Merknad:</strong> {weekDetailBooking.customerNote || '-'}</Typography>
              {weekDetailBooking.smsFailed && (
                <Alert severity="warning">
                  SMS-feil: {weekDetailBooking.smsErrorDescription || 'Ukjent feil'}
                </Alert>
              )}
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Dekkvurderinger</strong>
                </Typography>
                {(() => {
                  const reg = weekDetailBooking.registrationNumber.trim().toUpperCase();
                  if (!reg) {
                    return null;
                  }

                  const evalDetail = evaluationDetailsByRegistration[reg];
                  const evalError = evaluationDetailsErrorByRegistration[reg];
                  if (loadingEvaluationDetailsRegistration === reg) {
                    return <Typography variant="body2" color="text.secondary">Laster dekkplassering...</Typography>;
                  }

                  if (evalError) {
                    return <Typography variant="body2" color="error">{evalError}</Typography>;
                  }

                  // Prefer appointment-level vehicle location if present (returned from backend), fallback to latest evaluation vehicle.location
                  const appointmentLevelLocation = weekDetailBooking?.vehicleLocation;
                  const finalLocation = appointmentLevelLocation ?? evalDetail?.vehicle?.location ?? 'Ukjent';
                  if (finalLocation) {
                    return (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}><strong>Plassering: {finalLocation}</strong></Typography>
                      </Box>
                    );
                  }

                  return null;
                })()}
                {renderEvaluationLinks(weekDetailBooking.registrationNumber)}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {weekDetailBooking &&
            (weekDetailBooking.status === AppointmentStatus.Pending || weekDetailBooking.status === AppointmentStatus.Confirmed) && (
              <Button
                color="error"
                onClick={() => openCancelDialog(weekDetailBooking.id)}
              >
                Avlys
              </Button>
          )}
          <Button onClick={() => setWeekDetailAppointmentId(null)}>Lukk</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialog.open} onClose={() => setCancelDialog((prev) => ({ ...prev, open: false }))} fullWidth maxWidth="sm">
        <DialogTitle>Avlys booking</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Begrunnelse er valgfri, men blir inkludert i SMS dersom den fylles ut.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Begrunnelse (valgfri)"
            value={cancelDialog.reason}
            onChange={(event) => setCancelDialog((prev) => ({ ...prev, reason: event.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog((prev) => ({ ...prev, open: false }))}>Lukk</Button>
          <Button variant="contained" color="error" onClick={submitCancellation}>Avlys booking</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default AppointmentOverview;
